// src/components/ui/drawer/EventsDrawer.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Drawer, 
  IconButton, 
  Button, 
  TextField, 
  FormControlLabel, 
  Switch,
  Box,
  Typography,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import dayjs from 'dayjs';
import { useNotification } from '../../../context/NotificationContext';
import { useSchedule } from '../../../hooks/vehicle/useSchedule';
import '../../../assets/scss/components/ui/drawner/EventsDrawer.scss';
import { useLayout } from '../../../context/LayoutContext';

const EventsDrawer = ({ 
  open, 
  onClose, 
  operationId, 
  operationName,
  selectedRouteId = null // Nueva prop para soporte de rutas
}) => {
  const { theme } = useLayout();
  const { showNotification } = useNotification();
  
  // Hook actualizado con soporte de rutas
  const { 
    useEventsByDateRange, 
    addEvent, 
    updateEvent, 
    deleteEvent,
    isLoading
  } = useSchedule(operationId, selectedRouteId);
  
  // Estados
  const [mode, setMode] = useState('list');
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    startDate: dayjs(),
    endDate: dayjs(),
    allDay: true
  });
  
  // Fechas para consultar eventos (1 año completo)
  const startOfYear = useMemo(() => dayjs().startOf('year').format('YYYY-MM-DD'), []);
  const endOfYear = useMemo(() => dayjs().endOf('year').format('YYYY-MM-DD'), []);
  
  // Hook específico para eventos con soporte de rutas
  const { data: eventsData, isLoading: isLoadingEvents, error } = 
    useEventsByDateRange(startOfYear, endOfYear, {
      enabled: open && !!operationId
    });
  
  // Procesar eventos con memoización optimizada
  const processedEvents = useMemo(() => {
    if (!eventsData?.data) return [];
    
    // Crear mapa para evitar duplicados
    const eventsMap = new Map();
    
    eventsData.data.forEach(event => {
      if (!eventsMap.has(event.id)) {
        eventsMap.set(event.id, {
          id: event.id,
          title: event.title,
          description: event.description,
          startDate: event.start_date,
          endDate: event.end_date,
          allDay: event.all_day,
          operationRouteId: event.operation_route_id,
          routeName: event.route_name
        });
      }
    });
    
    // Convertir a array y ordenar por fecha
    const eventsArray = Array.from(eventsMap.values());
    return eventsArray.sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
  }, [eventsData]);

  // Actualizar eventos cuando cambien los datos
  useEffect(() => {
    setEvents(processedEvents);
  }, [processedEvents]);
  
  // Resetear formulario
  const resetForm = useCallback(() => {
    setFormData({
      title: '',
      description: '',
      startDate: dayjs(),
      endDate: dayjs(),
      allDay: true
    });
  }, []);
  
  // Handlers para cambio de modo
  const handleAddMode = useCallback(() => {
    resetForm();
    setMode('add');
  }, [resetForm]);
  
  const handleEditMode = useCallback((event) => {
    setSelectedEvent(event);
    setFormData({
      title: event.title,
      description: event.description || '',
      startDate: dayjs(event.startDate),
      endDate: dayjs(event.endDate),
      allDay: event.allDay
    });
    setMode('edit');
  }, []);
  
  const handleBackToList = useCallback(() => {
    setMode('list');
    setSelectedEvent(null);
    resetForm();
  }, [resetForm]);
  
  // Handlers para formulario
  const handleInputChange = useCallback((e) => {
    const { name, value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'allDay' ? checked : value
    }));
  }, []);
  
  const handleDateChange = useCallback((name, date) => {
    setFormData(prev => ({
      ...prev,
      [name]: date
    }));
  }, []);
  
  // Handlers para acciones CRUD con soporte de rutas
  const handleSubmitEvent = useCallback(async (e) => {
    e.preventDefault();
    
    try {
      const eventData = {
        title: formData.title,
        description: formData.description,
        startDate: formData.startDate.format('YYYY-MM-DD'),
        endDate: formData.endDate.format('YYYY-MM-DD'),
        allDay: formData.allDay,
        operationRouteId: selectedRouteId // Incluir ruta en el evento
      };
      
      if (mode === 'add') {
        await addEvent(eventData);
        showNotification({
          message: 'Evento creado exitosamente',
          severity: 'success'
        });
      } else {
        await updateEvent({
          eventId: selectedEvent.id,
          ...eventData
        });
        showNotification({
          message: 'Evento actualizado exitosamente',
          severity: 'success'
        });
      }
      
      handleBackToList();
    } catch (error) {
      console.error('Error al procesar evento:', error);
      showNotification({
        message: error.message || 'Error al procesar evento',
        severity: 'error'
      });
    }
  }, [formData, mode, selectedEvent, selectedRouteId, addEvent, updateEvent, showNotification, handleBackToList]);
  
  const handleDeleteEvent = useCallback(async (eventId) => {
    if (window.confirm('¿Estás seguro de eliminar este evento?')) {
      try {
        await deleteEvent(eventId);
        showNotification({
          message: 'Evento eliminado exitosamente',
          severity: 'success'
        });
      } catch (error) {
        console.error('Error al eliminar evento:', error);
        showNotification({
          message: error.message || 'Error al eliminar evento',
          severity: 'error'
        });
      }
    }
  }, [deleteEvent, showNotification]);

  // Estados consolidados de carga y error
  const isLoadingState = isLoadingEvents || isLoading;
  
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      className={`GM__events-drawer-container-${theme}`}
      PaperProps={{
        className: `GM__${theme}-events-drawer-paper`,
      }}
    >
      <div className={`GM__${theme}-events-drawer`}>
        {/* Header */}
        <div className={`GM__${theme}-events-drawer-header`}>
          <div className={`GM__${theme}-drawer-title-container`}>
            {mode !== 'list' && (
              <IconButton
                onClick={handleBackToList}
                className={`GM__${theme}-back-button`}
              >
                <KeyboardBackspaceIcon />
              </IconButton>
            )}
            <h2 className={`GM__${theme}-drawer-title`}>
              {mode === 'list'
                ? `Eventos - ${operationName || 'Operación'}`
                : mode === 'add'
                ? 'Añadir Evento'
                : 'Editar Evento'}
              {selectedRouteId && (
                <span className={`GM__${theme}-route-indicator`}>
                  - Ruta Específica
                </span>
              )}
            </h2>
          </div>
          <div className={`GM__${theme}-drawer-header-actions`}>
            {mode === 'list' && (
              <IconButton
                onClick={handleAddMode}
                className={`GM__${theme}-add-button`}
              >
                <AddIcon />
              </IconButton>
            )}
            <IconButton onClick={onClose} className={`GM__${theme}-close-button`}>
              <CloseIcon />
            </IconButton>
          </div>
        </div>
        
        <Divider />
        
        {/* Contenido */}
        <div className={`GM__${theme}-events-drawer-content ${mode}`}>
          {isLoadingState ? (
            <div className={`GM__${theme}-loading-container`}>
              <CircularProgress size={40} />
              <div className={`GM__${theme}-loading-message`}>
                Cargando eventos...
              </div>
            </div>
          ) : error ? (
            <div className={`GM__${theme}-error-message`}>
              {error.message || 'No se pudieron cargar los eventos'}
            </div>
          ) : mode === 'list' ? (
            <>
              {/* Lista de eventos */}
              {events.length > 0 ? (
                <List>
                  {events.map((event) => (
                    <ListItem 
                      key={event.id}
                      className={`GM__${theme}-event-item`}
                    >
                      <ListItemText
                        primary={event.title}
                        secondary={
                          <>
                            <Typography component="span" variant="body2">
                              {dayjs(event.startDate).format('DD/MM/YYYY')}
                              {' - '}
                              {dayjs(event.endDate).format('DD/MM/YYYY')}
                            </Typography>
                            <br />
                            <Typography component="span" variant="body2">
                              {event.description || 'Sin descripción'}
                            </Typography>
                            {event.routeName && (
                              <>
                                <br />
                                <Typography component="span" variant="caption" color="primary">
                                  Ruta: {event.routeName}
                                </Typography>
                              </>
                            )}
                          </>
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          aria-label="edit"
                          onClick={() => handleEditMode(event)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={() => handleDeleteEvent(event.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <div className={`GM__${theme}-empty-message`}>
                  {selectedRouteId ? 
                    'No hay eventos programados para esta ruta' : 
                    'No hay eventos programados'
                  }
                </div>
              )}
            </>
          ) : (
            <>
              {/* Formulario de evento */}
              <form onSubmit={handleSubmitEvent} className={`GM__${theme}-event-form`}>
                <TextField
                  label="Título"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  margin="normal"
                />
                
                <TextField
                  label="Descripción"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  fullWidth
                  multiline
                  rows={4}
                  margin="normal"
                />
                
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Box sx={{ display: 'flex', gap: 2, mt: 2, mb: 1 }}>
                    <DatePicker
                      label="Fecha de inicio"
                      value={formData.startDate}
                      onChange={(date) => handleDateChange('startDate', date)}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                    
                    <DatePicker
                      label="Fecha de fin"
                      value={formData.endDate}
                      onChange={(date) => handleDateChange('endDate', date)}
                      slotProps={{ textField: { fullWidth: true } }}
                      minDate={formData.startDate}
                    />
                  </Box>
                </LocalizationProvider>
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.allDay}
                      onChange={handleInputChange}
                      name="allDay"
                      color="primary"
                    />
                  }
                  label="Todo el día"
                />
                
                {selectedRouteId && (
                  <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
                    Este evento se asociará a la ruta seleccionada
                  </Typography>
                )}
                
                <Box sx={{ mt: 3 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <CircularProgress size={24} />
                    ) : mode === 'add' ? (
                      'Añadir Evento'
                    ) : (
                      'Actualizar Evento'
                    )}
                  </Button>
                </Box>
              </form>
            </>
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default EventsDrawer;